<template>
  <v-dialog
      v-model="visible"
      :width="dialogWidth"
      persistent
  >
    <v-overlay v-if="loading">
      <v-progress-circular indeterminate color="primary"/>
    </v-overlay>
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
    >
      <v-card-title>
        <v-row class="no-gutters">
          <span class="headline">Preview {{type?.name}}</span>
          <v-spacer/>
          <v-btn color="green" class="ml-1" @click="exportToJSON">Export</v-btn>
          <v-btn color="orange" class="ml-1" @click="onPressImportJson">Import</v-btn>
          <v-btn color="red darken-1" class="ml-1" @click="cancel">Cancel</v-btn>
          <v-btn color="blue darken-1" class="ml-1" @click="save">Save</v-btn>
        </v-row>
      </v-card-title>

      <v-card-text>
        <codemirror
            v-model="modelCollection"
            v-if="modelCollection.length <= 1000000"
            :options="cmOpt"
            class="cm-editor"
        />
        <div v-else>
          <v-alert type="error" dense>
            The content is too large to be displayed. Please export it to view the full content. </br>
            Is still possible to import a JSON file with updated content and proceed to Save. </br>
            Validations will still occur on Save.
          </v-alert>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="red darken-1" class="ml-1" @click="cancel">Cancel</v-btn>
        <v-btn color="blue darken-1" class="ml-1" @click="save">Save</v-btn>
      </v-card-actions>

      <input ref="uploaderJson"
             class="d-none"
             type="file"
             accept="application/json"
             @change="onFileChangedJson">
      <div class="resize-handle" @mousedown="startResize"></div>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import CodeMirror from "codemirror";
import {ViewRules} from "@/common/ViewRules";

import { codemirror } from "vue-codemirror";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/monokai.css";
import "codemirror/addon/edit/matchbrackets";

import "codemirror/addon/search/searchcursor";
import "codemirror/addon/search/match-highlighter";

import "codemirror/addon/lint/lint";
import "codemirror/addon/lint/lint.css";
import "codemirror/addon/lint/json-lint";

import "codemirror/mode/javascript/javascript.js";
import {deserialize, serialize} from "serializr";
import Store from "@/store";
import {TimeUtils} from "@/common/TimeUtils";
import {FirebaseJsonUtils} from "@/common/FirebaseJsonUtils";

export default {
  name: "r-dialog-preview-collection",
  props: {
    content: VueTypes.string.def(""),
  },
  components: { codemirror },
  data: () => ({
    visible: false,
    loading: true,
    resolve: null,
    reject: null,
    modelCollection: "",
    type: null,
    storeName: "",
    rules: ViewRules.READONLY,

    dialogWidth: 920,
    dialogHeight: 546,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 920,
    resizeStartHeight: 546,
    viewportPadding: 32,

    cmOpt: {
      mode: 'application/json',
      theme: 'monokai',
      tabSize: 4,
      lineNumbers: true,
      lineWrapping: true,
      viewportMargin: Infinity,
      matchBrackets: true,
      flattenSpans: true,
      highlightSelectionMatches: { showToken: true },
      gutters: ["CodeMirror-lint-markers"],
      lint: {
        tooltips: true,
        delay: 200,
        getAnnotations: (text, options, cm) => {
          const s = (text || "").trim();
          if (!s) return [];
          return CodeMirror.lint.json(text, options, cm);
        }
      }
    },
  }),
  methods: {
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;

      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;

      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 500;
      const minHeight = 350;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;

      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    save() {
      try {
        this.loading = true;
        let parsed = JSON.parse(this.modelCollection);
        const result = parsed.items.map(v => deserialize(this.type, v));
        let errors = [];
        let importedValues = [];
        result.forEach(v => {
          try {
            if (!v.valid()) {
              errors.push(v.uid + " is invalid");
            }
          } catch (e) {
            errors.push(v.uid + " is invalid: " + e);
          }
        });
        if (errors.length > 0) {
          let reason = errors.map(v => JSON.stringify(v)).join(',');
          this.loading = false;
          this.$alert(reason, "JSON contains errors", "error", {width: '800px'});
          return;
        }

        try {
          const savePromises = result.map(v =>
              Store.dispatch(this.storeName + "/removeValue", v)
                  .then(() => Store.dispatch(this.storeName + "/pushValue", v))
                  .then(() => importedValues.push(v))
          );
          Promise.all(savePromises).then(() => {
            this.resolve && this.resolve(importedValues);
            this.visible = false;
            this.loading = false;
          });
        } catch (e) {
          this.$alert("Failed to save values: " + e.message);
          this.loading = false;
        }
      } catch (e) {
        this.$alert("Invalid JSON! " + e.message);
      }
    },
    cancel() {
      this.loading = false;
      this.visible = false;
    },
    show(input, type, storeSuffix) {
      try {
        this.visible = true;
        this.type = type;
        this.storeName = "redactor-store-" + storeSuffix;
        let data = {};
        let errors = [];
        data.items = input.map(v => {
          try {
            return serialize(type, v);
          } catch (e) {
            errors.push(v.uid + " is invalid: " + e);
          }
        }).filter(v => v !== undefined && v !== null);
        data.key = type.name.toLowerCase();
        this.modelCollection = JSON.stringify(data, null, 4);
        this.loading = false;

        if (errors.length > 0) {
          let reason = errors.map(v => JSON.stringify(v)).join(',');
          this.$alert(reason, "Some items could not be serialized", "error", {width: '800px'});
        }
        return new Promise((resolve, reject) => {
          this.resolve = resolve;
          this.reject = reject;
        });
      } catch (e) {
        this.$alert("This config contains errors: " + e.message);
        this.loading = false;
      }
    },
    exportToJSON() {
      try {
        const adminPanelJson = JSON.parse(this.modelCollection);

        const firebaseJson = {};
        adminPanelJson.items.forEach(item => {
          const firebaseItem = FirebaseJsonUtils.toFirebaseJson(item);
          if (firebaseItem !== undefined && firebaseItem.uid) {
            firebaseJson[firebaseItem.uid] = firebaseItem;
          }
        });

        const jsonString = JSON.stringify(firebaseJson, null, 2);
        let blob = new Blob([jsonString], {type: "application/json"});
        let saver = require('file-saver');

        const typeName = this.type.name.toLowerCase();
        const fileName = `mira-merge---export-${typeName}-collection-${TimeUtils.now()}.json`;
        saver.saveAs(blob, fileName);
      } catch (e) {
        this.$alert("Failed to export: " + e.message);
      }
    },
    parseCurrentModelCollection() {
      try {
        return JSON.parse(this.modelCollection);
      } catch (e) {
        return { items: [] };
      }
    },
    onPressImportJson() {
      this.$refs.uploaderJson.click()
    },
    onFileChangedJson(e) {
      if (!e.target.files || !e.target.files[0]) {
        this.$refs.uploaderJson.value = "";
        return;
      }
      this.loading = true;
      this.selectedFile = e.target.files[0]
      this.selectedFile.text()
          .then((data)=>{
            try {
              let parsedData = JSON.parse(data);

              if (!parsedData.items || !Array.isArray(parsedData.items)) {
                const items = [];
                for (const [uid, value] of Object.entries(parsedData)) {
                  if (value && typeof value === 'object') {
                    items.push({ uid, ...value });
                  }
                }
                if (items.length === 0) {
                  this.$alert("Invalid data: no items found");
                  this.loading = false;
                  return;
                }
                parsedData = {
                  key: this.type.name.toLowerCase(),
                  items: items
                };
              }

              if (!data || parsedData["key"] !== this.type.name.toLowerCase()) {
                this.$alert("Invalid data: key mismatch");
                this.loading = false;
                return;
              }

              const currentData = this.parseCurrentModelCollection();
              const template = FirebaseJsonUtils.buildTemplate(currentData.items);

              let errors = [];
              let warnings = [];
              let updatedValues = {};
              updatedValues.key = parsedData.key;
              updatedValues.items = [];

              parsedData.items.forEach((v) => {
                const normalized = template ? FirebaseJsonUtils.normalizeWithTemplate(v, template) : v;

                try {
                  let parsedDataItem = deserialize(this.type, normalized);

                  if (!parsedDataItem.valid()) {
                    warnings.push((v.uid || 'unknown') + " has validation warnings");
                  }

                  if (typeof parsedDataItem.resetToValidAutomatic === "function") {
                    parsedDataItem.resetToValidAutomatic();
                  }

                  updatedValues.items.push(serialize(this.type, parsedDataItem));
                } catch (e) {
                  updatedValues.items.push(normalized);
                  errors.push((v.uid || 'unknown') + ": " + e);
                }
              });

              this.modelCollection = JSON.stringify(updatedValues, null, 4);

              if (errors.length > 0) {
                this.loading = false;
                const summary = `Imported ${updatedValues.items.length} items. ${errors.length} items had deserialization errors and were added as raw data — please review before saving.`;
                this.$alert(summary, "Import completed with warnings", "warning", {width: '800px'});
                return;
              }

              if (warnings.length > 0) {
                this.$alert(`Imported ${updatedValues.items.length} items successfully. ${warnings.length} items have validation warnings.`);
              } else {
                this.$alert(`File imported successfully (${updatedValues.items.length} items)`)
              }
              this.loading = false;
            } catch (error) {
              this.$alert("Failed to parse JSON: " + error.message);
              this.loading = false;
            }
          }).catch((error) => {
        this.$alert("Failed to read file: " + error.message);
        this.loading = false;
      });
      this.$refs.uploaderJson.value = "";
    },
    onEscKey(e) {
      if (e.key === 'Escape') {
        this.cancel();
      }
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        window.addEventListener('keydown', this.onEscKey);
      } else {
        window.removeEventListener('keydown', this.onEscKey);
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onEscKey);
  }
}

</script>

<style>
.CodeMirror {
  height: auto;
}

.cm-editor {
  border: 1px solid #fff;
  border-radius: 6px;
  font-size: 14px;
}

.CodeMirror-matchingbracket {
  background-color: rgba(0, 120, 255, 0.5);
  color: white;
}

.CodeMirror-nonmatchingbracket {
  background-color: rgba(255, 0, 0, 0.1);

}

.cm-matchhighlight {
  background: rgba(0, 150, 255, 0.2);
}

.CodeMirror-lint-tooltip {
  max-width: 520px;
  line-height: 1.3;
  white-space: pre-wrap;
  z-index: 99999;
}

.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}

.resizable-card__body {
  flex: 1;
  overflow: auto;
}

.resizable-card__actions {
  flex-shrink: 0;
}

.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
</style>
